import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { useStyles } from './PaymentSchedule.styles'
import {
  Card,
  FormControl,
  FormControlLabel,
  Link,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material'
import { FormikProps, useFormikContext } from 'formik'
import { DateTime } from 'luxon'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFileContract } from '@fortawesome/pro-regular-svg-icons'

import { useParams } from 'react-router-dom'
import { useAccountId } from '@labrav/common'
import type {
  AgreementBasedOffer,
  PaymentSchedule as SubscriptionPaymentSchedule,
} from '../../../../modules/subscription/reducer'
import type { Installments } from '../../../../types/flyout'
import { useSelector } from '../../../../store'
import { awsMaxScheduledInstallments } from '../../../../common/utils/constants'
import { PaymentScheduleV2 } from '../../../../common/components/PaymentScheduleV2/PaymentScheduleV2'

interface PaymentScheduleProps {
  subscriptionId: string
  currencySymbol?: string
}

const EMPTY_INSTALLMENT = {
  amount: null,
  paymentDate: '',
  currency: 'USD',
} as const satisfies Installments

export const isBlankInstallment = (installment?: Installments) =>
  !installment || (!installment.paymentDate && installment.amount == null)

export const parsePaymentDate = (paymentDate?: string) => {
  if (!paymentDate) {
    return DateTime.invalid('missing-payment-date')
  }
  const isoDate = DateTime.fromISO(paymentDate)
  if (isoDate.isValid) {
    return isoDate
  }
  return DateTime.fromSQL(paymentDate)
}

export const getManualInstallments = (
  installments: Array<
    Pick<Installments, 'amount' | 'currency' | 'paymentDate'>
  > = []
) => {
  const unbilledInstallments = installments.filter(installment => {
    const paymentDate = parsePaymentDate(installment.paymentDate)
    if (!paymentDate.isValid) {
      return false
    }
    return paymentDate.isValid && paymentDate > DateTime.now()
  })
  if (!unbilledInstallments.length) {
    return [{ ...EMPTY_INSTALLMENT }]
  }
  return [...unbilledInstallments, { ...EMPTY_INSTALLMENT }]
}

export const getExistingContractInstallments = (
  paymentSchedule: SubscriptionPaymentSchedule[] = [],
  installments: Installments[] = []
) => {
  if (paymentSchedule.length > 0) {
    return getManualInstallments(
      paymentSchedule.map(payment => ({
        amount: payment.amount,
        currency: payment.currency,
        paymentDate: payment.paymentDate,
      }))
    )
  }
  return getManualInstallments(installments)
}

export const PaymentSchedule = ({
  subscriptionId,
  currencySymbol,
}: PaymentScheduleProps) => {
  const formikProps = useFormikContext() as FormikProps<AgreementBasedOffer>
  const { values, setFieldValue } = formikProps
  const { classes } = useStyles()
  const [updatedRandomKey, setUpdatedRandomKey] = useState(0)
  const hasInitializedInstallments = useRef(false)

  const subscriptionDetail = useSelector(
    state =>
      state?.subscriptions?.currentSubscription[subscriptionId] ||
      state?.modalSubscription?.currentSubscription?.[subscriptionId]
  )

  const { cloud: routeCloud, accountId: routeAccountId } = useParams<{
    cloud?: string
    accountId?: string
  }>()
  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const accountId = routeAccountId || fallbackAccountId

  const subscriptionDetailUrl = `/flyout/${routeCloud}/${accountId}/subscriptions/${subscriptionId}/detail`

  const existingContractInstallments = useMemo(
    () =>
      getExistingContractInstallments(
        subscriptionDetail?.paymentSchedule || [],
        subscriptionDetail?.offerDetails?.installments || []
      ),
    [
      subscriptionDetail?.offerDetails?.installments,
      subscriptionDetail?.paymentSchedule,
    ]
  )

  const hasUnbilledInstallments = useMemo(
    () =>
      existingContractInstallments.some(
        installment => !isBlankInstallment(installment)
      ),
    [existingContractInstallments]
  )

  const replaceInstallments = useCallback(
    async (
      continueFromExistingContract: boolean,
      installments: Installments[]
    ) => {
      await setFieldValue(
        'continueFromExistingContract',
        continueFromExistingContract
      )
      await setFieldValue('installments', installments)
      setUpdatedRandomKey(currentKey => currentKey + 1)
    },
    [setFieldValue]
  )

  useEffect(() => {
    if (hasInitializedInstallments.current) {
      return
    }

    const shouldSeedInstallments =
      !values.installments?.length ||
      (values.installments.length === 1 &&
        isBlankInstallment(values.installments[0]))

    if (
      shouldSeedInstallments &&
      existingContractInstallments.some(
        installment => !isBlankInstallment(installment)
      )
    ) {
      hasInitializedInstallments.current = true
      void replaceInstallments(true, existingContractInstallments)
      return
    }

    if (values.continueFromExistingContract !== undefined) {
      hasInitializedInstallments.current = true
      return
    }

    hasInitializedInstallments.current = true
    void replaceInstallments(
      true,
      shouldSeedInstallments
        ? existingContractInstallments
        : values.installments || [{ ...EMPTY_INSTALLMENT }]
    )
  }, [
    existingContractInstallments,
    replaceInstallments,
    values.continueFromExistingContract,
    values.installments,
  ])

  const handleContinueChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      if (value === 'continue') {
        void replaceInstallments(true, existingContractInstallments)
      } else {
        void replaceInstallments(false, [{ ...EMPTY_INSTALLMENT }])
      }
    },
    [existingContractInstallments, replaceInstallments]
  )

  const selectedOption =
    values.continueFromExistingContract === false ? 'skip' : 'continue'

  return (
    <div className={classes.root}>
      <PaymentScheduleV2
        formik={formikProps as unknown as FormikProps<Record<string, unknown>>}
        hideOuterBorder
        subHeading={
          <>
            {hasUnbilledInstallments ? (
              <div className={classes.installmentSelection}>
                <Typography className={classes.heading}>
                  The existing contract still has unbilled installments of{' '}
                  <strong>
                    {currencySymbol || '$'}
                    {
                      subscriptionDetail?.subscription
                        ?.remainingInstallementAmount
                    }
                  </strong>
                  . Choose how these installments should be added to the offer.{' '}
                  <Link
                    href={subscriptionDetailUrl}
                    target="_blank"
                    underline="none"
                    className={classes.linkStyle}
                  >
                    View details
                  </Link>{' '}
                  <span
                    className={classes.iconButton}
                    onClick={() => window.open(subscriptionDetailUrl, '_blank')}
                  >
                    <FontAwesomeIcon
                      icon={faFileContract}
                      className={classes.fileContractIcon}
                    />
                  </span>
                </Typography>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    value={selectedOption}
                    onChange={handleContinueChange}
                    className={classes.radioGroup}
                  >
                    <Card
                      className={clsx(classes.optionCard, {
                        [classes.selectedCard]: selectedOption === 'continue',
                      })}
                    >
                      <FormControlLabel
                        value="continue"
                        control={<Radio />}
                        label={
                          <div className={classes.cardLabelContent}>
                            <Typography className={classes.cardTitle}>
                              Continue from existing contract
                            </Typography>
                            <Typography className={classes.cardDescription}>
                              Pre-fill the unbilled installments and their
                              invoice dates below. I can add new installments
                              after them.
                            </Typography>
                          </div>
                        }
                        className={classes.cardFormLabel}
                      />
                    </Card>
                    <Card
                      className={clsx(classes.optionCard, {
                        [classes.selectedCard]: selectedOption === 'skip',
                      })}
                    >
                      <FormControlLabel
                        value="skip"
                        control={<Radio />}
                        label={
                          <div className={classes.cardLabelContent}>
                            <Typography className={classes.cardTitle}>
                              Skip and enter installments manually
                            </Typography>
                            <div className={classes.cardDescriptionWrapper}>
                              <Typography className={classes.cardDescription}>
                                I will add the remaining installments from the
                                previous contract and any new installments
                              </Typography>
                              <Typography
                                className={clsx(
                                  classes.cardDescription,
                                  classes.cardDescriptionSecondLine
                                )}
                              >
                                myself.
                              </Typography>
                            </div>
                          </div>
                        }
                        className={classes.cardFormLabel}
                      />
                    </Card>
                  </RadioGroup>
                </FormControl>
              </div>
            ) : (
              <div className={classes.upperDiv}>
                <Typography
                  className={classes.heading}
                  data-testid="pending-amount"
                >
                  There are no remaining unbilled installments on the existing
                  contract.{' '}
                  <Link
                    href={subscriptionDetailUrl}
                    target="_blank"
                    underline="none"
                    className={classes.linkStyle}
                  >
                    View details
                  </Link>{' '}
                  <span
                    className={classes.iconButton}
                    onClick={() => window.open(subscriptionDetailUrl, '_blank')}
                  >
                    <FontAwesomeIcon
                      icon={faFileContract}
                      className={classes.fileContractIcon}
                    />
                  </span>
                </Typography>
              </div>
            )}
          </>
        }
        addButtonName={'Add payment'}
        buttonCounterName={'installments'}
        shouldShowTotal={true}
        installmentsLimit={awsMaxScheduledInstallments}
        updatedRandomKey={updatedRandomKey}
      />
    </div>
  )
}
